import { ReactNode } from 'react';
import { createUseStyles, useTheme } from 'react-jss';
import classnames from 'classnames';
import { Theme } from '@app/theme';
import { NavLink } from 'react-router-dom';

const useStyles = createUseStyles((theme: Theme) => ({
  navLink: {
    ...theme.fonts.h5,
    marginLeft: -theme.marginBase,
    marginRight: -theme.marginBase,
    marginBottom: theme.marginBase,
    padding: theme.marginBase,
    borderRadius: theme.borderRadius.std,
    display: 'flex',
    alignItems: 'center',

    [theme.media.tablet]: {
      padding: 0,
      justifyContent: 'center',
      width: theme.height.small,
      height: theme.height.small,
    },
  },
  grayish: {
    color: theme.colors.inkSecondary,
  },
  text: {
    flex: 1,
    [theme.media.tablet]: {
      display: 'none',
    },
  },
  icon: {
    fontSize: theme.iconSize.large,
    marginRight: theme.marginBase,

    [theme.media.tablet]: {
      marginRight: 0,
    },
  },
  navLinkActive: {
    color: theme.colors.accentAccessible,
  },
  indicator: {
    fontSize: theme.iconSize.regular,
  },
  chipLine: {
    display: 'flex',
    alignItems: 'center',
  },
  chip: {
    marginLeft: theme.marginBase,
  },
  horizontalIcon: {
    [theme.media.tablet]: {
      marginRight: theme.marginBase,
    },
  },
  horizontalText: {
    [theme.media.tablet]: {
      display: 'block',
      flex: 'inherit',
    },
  },
  horizontalNavLink: {
    [theme.media.tablet]: {
      padding: 0,
      paddingLeft: theme.marginBase * 2,
      margin: 0,
      width: theme.maxContentWidth.smallBlock,
    },
  },
}));

interface Props {
  text: ReactNode;
  to: string;
  icon?: string;
  iconClassName?: string;
  indicator?: string;
  indicatorClassName?: string;
  className?: string;
  variant?: 'grayish';
  end?: boolean;
  horizontal?: boolean;
}

export const SideBarItem = ({
  to,
  text,
  iconClassName,
  icon,
  className,
  indicator,
  indicatorClassName,
  variant,
  horizontal,
  ...props
}: Props) => {
  const theme: Theme = useTheme();
  const classes = useStyles({ ...props, theme });

  return (
    <NavLink
      className={({ isActive }) =>
        classnames(
          classes.navLink,
          {
            [classes.grayish]: variant === 'grayish',
            [classes.horizontalNavLink]: horizontal,
            [classes.navLinkActive]: isActive,
          },
          className,
        )
      }
      to={to}
      {...props}
    >
      {icon && (
        <i
          className={classnames(
            classes.icon,
            icon,
            {
              [classes.horizontalIcon]: horizontal,
            },
            iconClassName,
          )}
        />
      )}
      {typeof text === 'string' ? (
        <div
          className={classnames(classes.text, {
            [classes.horizontalText]: horizontal,
          })}
        >
          {text}
        </div>
      ) : (
        text
      )}
      {indicator && (
        <i
          className={classnames(
            classes.indicator,
            indicator,
            indicatorClassName,
          )}
        />
      )}
    </NavLink>
  );
};
